import React, { useState, useEffect, useRef, useCallback } from 'react'
import Link from '../Link'
import NavLink from '../NavLink'
import Language from '../Language'
import SuperNav from './SuperNav'
import LowerMobileNav from './LowerMobileNav'
import Logo from '../../../assets/images/Ouster_Logo_TM_Horiz_White_RGB.svg'
import MobileLogo from '../../../assets/images/m-logo.svg'
import TabletLogo from '../../../assets/images/logo-horz-tablet.svg'
import HamBurger from '../../../assets/images/HamBurger.svg'
import CloseButton from '../../../assets/images/close.svg'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import useWindowSize from '../../hooks/useWindowSize'
import { dynamicSort } from '../../utils'
import * as styles from './index.module.css'

const Navigation = ({
  smartToggleHeight,
  links,
  superNavLinks,
  solid,
  fixed,
  bgColor,
  smartSolid,
  langs,
  locale,
  smart,
  latestBlogPost,
  pageAlert,
}) => {
  const { width } = useWindowSize()
  const targetRef = useRef()
  const [mouseOverChildSection, setMouseOverChildSection] = useState(false)
  const [sideDrawerVisible, setSideDrawerVisible] = useState(false)
  const [smartState, setSmartState] = useState(smart)
  const [transparent, setTransparent] = useState(true)
  const [visible, setVisible] = useState(true)
  const isMobile = width <= 767
  const isTablet = width >= 767 && width <= 1199
  const [prevScrollpos, setPrevScrollpos] = useState(200)
  const [toggleHeight, setToggleHeight] = useState(0)

  const setChildData = (data) => {
    if (data && !mouseOverChildSection) {
      setMouseOverChildSection(true)
    }
  }

  const handleHamburgerClick = (event) => {
    setSideDrawerVisible((prevState) => !prevState)
    disableBodyScroll(document.querySelector('#sideDrawer'))
  }

  const handleCrossClick = (event) => {
    setSideDrawerVisible((prevState) => !prevState)
    enableBodyScroll(document.querySelector('#sideDrawer'))
  }

  const handleScroll = useCallback(() => {
    const currentScrollPos = window.pageYOffset
    setVisible(
      currentScrollPos < toggleHeight || prevScrollpos > currentScrollPos
    )
    setTransparent(currentScrollPos < Math.min(window.innerHeight, 821) - 90)
    if (
      document.querySelector('[class^="index-module--homePageHero"]') === null
    ) {
      setTransparent(false)
    }
    setPrevScrollpos(currentScrollPos)
  }, [prevScrollpos, toggleHeight])

  useEffect(() => {
    if (smartState) {
      setSmartState(true)
      setToggleHeight(smartToggleHeight ? smartToggleHeight : 90)
      window.addEventListener('scroll', handleScroll)
    }
    return function cleanup() {
      clearAllBodyScrollLocks()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, smartState, smartToggleHeight])

  useEffect(() => {
    if (
      document.querySelector('[class^="index-module--homePageHero"]') === null
    ) {
      setTransparent(false)
    }
  }, [])

  useEffect(() => {
    if (!isTablet || !isMobile) {
      document.body.style.overflow = ''
    }
    if ((isTablet || isMobile) && sideDrawerVisible) {
      document.body.style.overflow = 'hidden'
    }
  }, [isTablet, isMobile, sideDrawerVisible])

  const displayedLinks = links.filter((link) => !link.hide)

  const groupBy = (key) => (array) =>
    array
      .map((item, index) => {
        if (item[key] === null) {
          item[key] = index
        }
        return item
      })
      .reduce((objectsByKeyValue, obj) => {
        const value = obj[key]
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj)
        return objectsByKeyValue
      }, {})

  const groupByLinkGroup = groupBy('groupId')

  const linkGroups = Object.entries(groupByLinkGroup(displayedLinks)).map(
    ([key, value]) =>
      value.length === 1
        ? value[0]
        : {
            label: value[0].label,
            hoverGradient: null,
            inverted: value[0].inverted,
            hide: value[0].hide,
            defaultUrl: value[0].defaultUrl,
            order: value[0].order,
            childLinks: [{ url: '' }, { url: '' }],
            links: value,
          }
  )

  //Dynamic Classes
  let customStyles = {}
  let navFillClass =
    solid || sideDrawerVisible || (smartSolid && smartState)
      ? `${styles.solidNav}`
      : ''

  let transparentClass =
    transparent && !sideDrawerVisible
      ? `${styles.transparentNav}`
      : `${styles.opaqueNav}`

  let fixedClass = smartState || fixed ? `${styles.fixedNav}` : styles.relative

  if (bgColor) {
    customStyles['backgroundColor'] = bgColor
  }

  return (
    <div className={styles.container}>
      <nav
        role="navigation"
        className={`${
          styles.navigationContainer
        } clearfix ${navFillClass} ${fixedClass} ${
          !visible && smartState
            ? `${styles.hiddenNavigation} hiddenNavigation`
            : ''
        } ${transparentClass}`}
        style={customStyles}
      >
        <SuperNav
          superNavLinks={superNavLinks}
          locale={locale}
          langs={langs}
          pageAlert={pageAlert}
        />

        <div className={styles.navigationFlexContainer}>
          <ul className={`${styles.navigation} ${styles.navigationLeft}`}>
            <li className={styles.logo} key="-1">
              <Link to={locale === 'en-us' ? '/' : `/${locale}/`}>
                {!isMobile && (
                  <Logo
                    style={{
                      width: '100%',
                      height: '100%',
                      marginLeft: '-21px',
                    }}
                  />
                )}
                {isMobile && (
                  <MobileLogo style={{ width: '100%', height: '100%' }} />
                )}
              </Link>
            </li>
          </ul>

          {!isMobile && !isTablet && (
            <ul className={`${styles.navigation} ${styles.navigationCenter}`}>
              {linkGroups
                .sort(dynamicSort('order'))
                .map(
                  (
                    {
                      label,
                      childLinks,
                      inverted,
                      hoverGradient,
                      defaultUrl,
                      links,
                    },
                    idx
                  ) => {
                    const navLinkProps = {
                      label,
                      childLinks: childLinks
                        ? childLinks.filter((childLink) => !childLink.hide)
                        : [],
                      inverted,
                      hoverGradient,
                      defaultUrl,
                      latestBlogPost,
                      locale,
                      links,
                    }

                    return (
                      childLinks &&
                      childLinks[0].url.indexOf('talk-to-sales') === -1 && (
                        <NavLink
                          key={idx}
                          {...navLinkProps}
                          updateChildData={setChildData}
                        />
                      )
                    )
                  }
                )}
            </ul>
          )}

          <ul className={`${styles.navigation} ${styles.navigationRight}`}>
            {
              <NavLink
                key={`talk-to-sales`}
                {...displayedLinks.filter(
                  ({ childLinks }) =>
                    childLinks &&
                    childLinks[0].url.indexOf('talk-to-sales') !== -1
                )[0]}
                updateChildData={setChildData}
                list={false}
              />
            }
          </ul>
        </div>

        <div className={styles.mobileNavigation} ref={targetRef}>
          <div className={styles.outerMobileHeader}>
            <div className={styles.mobileHeader}>
              <div>
                <Link to="/">
                  <MobileLogo className={styles.logoText} />
                </Link>
              </div>
              <div
                className={
                  locale.indexOf('jp') !== -1 ? styles.jpButtonStyle : ``
                }
              >
                {!sideDrawerVisible && (
                  <NavLink
                    {...displayedLinks.filter(
                      ({ childLinks }) =>
                        childLinks &&
                        childLinks[0].url.indexOf('talk-to-sales') !== -1
                    )[0]}
                    list={false}
                    updateChildData={setChildData}
                  />
                )}
              </div>
              <div>
                {!sideDrawerVisible && (
                  <HamBurger
                    className={styles.hamburger}
                    onClick={handleHamburgerClick}
                  />
                )}
              </div>
              {sideDrawerVisible && (
                <div>
                  <CloseButton
                    className={styles.closeButton}
                    onClick={handleCrossClick}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            className={[
              styles.outerMobileHeader,
              styles.outerTabletHeader,
            ].join(' ')}
          >
            <div className={styles.mobileHeader}>
              {!sideDrawerVisible && (
                <div>
                  <HamBurger
                    className={styles.hamburger}
                    onClick={handleHamburgerClick}
                  />
                </div>
              )}
              {sideDrawerVisible && (
                <div>
                  <CloseButton
                    className={styles.closeButton}
                    onClick={handleCrossClick}
                  />
                </div>
              )}
              <div>
                <Link to="/">
                  <TabletLogo className={styles.logoText} />
                </Link>
              </div>

              <div>
                <NavLink
                  {...displayedLinks.filter(
                    ({ childLinks }) =>
                      childLinks &&
                      childLinks[0].url.indexOf('talk-to-sales') !== -1
                  )[0]}
                  list={false}
                  updateChildData={setChildData}
                />
              </div>
            </div>
          </div>

          <div
            id="sideDrawer"
            className={`${styles.sideDrawer} ${
              sideDrawerVisible ? styles.sideDrawerOpen : ''
            }`}
          >
            <ul className={styles.mobileNavList}>
              {linkGroups
                .sort(dynamicSort('order'))
                .map(
                  (
                    {
                      label,
                      childLinks,
                      inverted,
                      hoverGradient,
                      defaultUrl,
                      links,
                    },
                    idx
                  ) => {
                    const navLinkProps = {
                      label,
                      childLinks: childLinks
                        ? childLinks.filter((childLink) => !childLink.hide)
                        : [],
                      inverted,
                      hoverGradient,
                      defaultUrl,
                      latestBlogPost,
                      locale,
                      links,
                    }

                    return (
                      <NavLink
                        key={idx}
                        {...navLinkProps}
                        updateChildData={setChildData}
                      />
                    )
                  }
                )}

              <LowerMobileNav superNavLinks={superNavLinks} />
              {isMobile && <Language langs={langs} />}
            </ul>
          </div>
        </div>
      </nav>
    </div>
  )
}

export default Navigation
